import useMedia from 'common/hooks/useMedia'
import { fNumber, fPercent } from 'common/utils/formatNumber'
import { useIsFishSpecie, useSpeciesName } from 'modules/pond/api/selector'
import { SpeciesName } from 'modules/pond/constants'
import { useShrimpSalesSummary } from 'modules/shrimpSales/api/queries'
import { formatTotalWeight } from 'modules/shrimpSales/utils/saleUnit'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Grid, Skeleton, Stack, Typography, useTheme } from '@mui/material'

import { Icon } from '@iconify/react'

interface SummaryCardProps {
  icon: ReactNode
  iconBgColor: string
  label: string
  value: ReactNode
  unit?: string
  highlight?: boolean
  isMobile: boolean
}

const SummaryCard = ({ icon, iconBgColor, label, value, unit, highlight, isMobile }: SummaryCardProps) => {
  const theme = useTheme()

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: highlight ? '#27AE60' : 'divider',
        bgcolor: highlight ? 'rgba(39, 174, 96, 0.05)' : 'transparent',
        p: isMobile ? 2 : 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
        height: '100%',
      }}
    >
      <Box display="flex" alignItems="center" gap={2}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: iconBgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography variant="body1" color="text.secondary">
          {label}
        </Typography>
      </Box>

      <Box display="flex" alignItems="baseline" gap={1}>
        <Typography
          color={highlight ? theme.palette.primary.main : 'text.primary'}
          variant={isMobile ? 'h5' : 'h4'}
          fontWeight={700}
        >
          {value}
        </Typography>
        {unit && <Typography color="text.secondary">{unit}</Typography>}
      </Box>
    </Box>
  )
}

const ShrimpSalesRecordBody = () => {
  const { t } = useTranslation('shrimpSales')
  const { data: summary, isFetching } = useShrimpSalesSummary()
  const isMobile = useMedia('sm')
  const theme = useTheme()
  const speciesName = useSpeciesName()
  // "จำนวนตัว" (การ์ดสรุปและคอลัมน์ "จำนวน") โชว์เฉพาะบ่อปลาตามที่ตกลงกัน —
  // บ่อกุ้งไม่โชว์แม้ backend จะส่ง totalShrimpSaleCount/totalQuantityCount มาให้
  const isFish = useIsFishSpecie()

  // เรียงตามยอดขาย (revenue) มากไปน้อย ให้ตรงกับ subtitle "เรียงตามยอดขาย"
  const shrimps = [...(summary?.categories ?? [])]
    .sort((a, b) => (b.totalRevenue ?? 0) - (a.totalRevenue ?? 0))
    .map((el) => ({
      name: el.name,
      weightAmount: el.totalQuantityInKg,
      priceAmount: el.totalRevenue,
      percentage: el.percentage,
      pieceCount: el.totalQuantityCount,
    }))

  const speciesIconSrc =
    speciesName === SpeciesName.Shrimp
      ? '/static/icons/growth/shrimp.svg'
      : '/static/icons/growth/fish.svg'

  const totalWeight = formatTotalWeight(summary?.totalShrimpSaleQuantityInKg ?? 0)

  return (
    <Box sx={{ p: 2 }}>
      {/* ราคารวม + น้ำหนักรวม + จำนวนตัว — จำนวนตัวมาจาก backend ตรง ๆ
          (DOF-1313/1314) ห้ามคำนวณเองจาก totalShrimpSaleQuantityInKg เพราะ
          แต่ละบิลในรอบอาจใช้ "ไซส์" ไม่เท่ากัน */}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={isFish ? 12 : 6}>
          <SummaryCard
            isMobile={isMobile}
            highlight
            icon={<Icon icon="mdi:cash" width={32} height={32} color="#FFFFFF" />}
            iconBgColor={theme.palette.primary.main}
            label={t('total.price')}
            value={
              isFetching ? <Skeleton width={120} /> : `฿${fNumber(summary?.totalShrimpSaleRevenue ?? 0)}`
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <SummaryCard
            isMobile={isMobile}
            icon={<Icon icon="mdi:scale-balance" width={32} height={32} color={theme.palette.primary.main} />}
            iconBgColor="rgba(39, 174, 96, 0.1)"
            label={t('total.weight')}
            value={isFetching ? <Skeleton width={80} /> : totalWeight.value}
            unit={t(totalWeight.unit === 'ton' ? 'unit.ton' : 'kg')}
          />
        </Grid>

        {isFish && (
          <Grid item xs={12} sm={6}>
            <SummaryCard
              isMobile={isMobile}
              icon={
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: theme.palette.primary.main,
                    WebkitMaskImage: `url(${speciesIconSrc})`,
                    maskImage: `url(${speciesIconSrc})`,
                    WebkitMaskSize: 'contain',
                    maskSize: 'contain',
                    WebkitMaskRepeat: 'no-repeat',
                    maskRepeat: 'no-repeat',
                    WebkitMaskPosition: 'center',
                    maskPosition: 'center',
                  }}
                />
              }
              iconBgColor="rgba(39, 174, 96, 0.1)"
              label={t('piece.count')}
              value={
                isFetching ? (
                  <Skeleton width={80} />
                ) : summary?.totalShrimpSaleCount != null ? (
                  fNumber(summary.totalShrimpSaleCount, { disabledDecimal: true })
                ) : (
                  '-'
                )
              }
              unit={t('unit.piece')}
            />
          </Grid>
        )}
      </Grid>

      {/* แยกตามประเภท — ตารางเรียงตามยอดขาย พร้อม progress bar สัดส่วน */}
      <Box mt={4}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography fontWeight={600}>{t('breakdown.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('breakdown.subtitle', { count: shrimps.length })}
          </Typography>
        </Stack>

        <Box sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Grid container sx={{ bgcolor: 'grey.50', px: 2, py: 1.25 }}>
            <Grid item xs={isFish ? 2.5 : 3}>
              <Typography variant="body2" fontWeight={600}>
                {t('type')}
              </Typography>
            </Grid>
            <Grid item xs={isFish ? 3.5 : 4}>
              <Typography variant="body2" fontWeight={600}>
                {t('proportion')}
              </Typography>
            </Grid>
            <Grid item xs={isFish ? 2 : 2.5}>
              <Typography variant="body2" fontWeight={600} align="right">
                {t('total.weight')}
              </Typography>
            </Grid>
            {isFish && (
              <Grid item xs={2}>
                <Typography variant="body2" fontWeight={600} align="right">
                  {t('amount')}
                </Typography>
              </Grid>
            )}
            <Grid item xs={isFish ? 2 : 2.5}>
              <Typography variant="body2" fontWeight={600} align="right">
                {t('total.price')}
              </Typography>
            </Grid>
          </Grid>

          {isFetching
            ? [0, 1, 2].map((n) => (
                <Grid
                  container
                  key={n}
                  alignItems="center"
                  sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                >
                  <Grid item xs={12}>
                    <Skeleton height={24} />
                  </Grid>
                </Grid>
              ))
            : shrimps.map((el, index) => (
                <Grid
                  container
                  key={el.name}
                  alignItems="center"
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderTop: index === 0 ? 'none' : '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Grid item xs={isFish ? 2.5 : 3}>
                    <Typography fontWeight={600}>{el.name}</Typography>
                  </Grid>
                  <Grid item xs={isFish ? 3.5 : 4}>
                    <Stack direction="row" alignItems="center" spacing={1.5} pr={2}>
                      <Box
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${Math.min(Math.max(el.percentage ?? 0, 0), 100)}%`,
                            bgcolor: theme.palette.primary.main,
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 44 }}>
                        {fPercent(el.percentage)}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={isFish ? 2 : 2.5}>
                    <Typography align="right">
                      {fNumber(el.weightAmount)} {t('kg')}
                    </Typography>
                  </Grid>
                  {isFish && (
                    <Grid item xs={2}>
                      <Typography align="right">
                        {el.pieceCount != null
                          ? `${fNumber(el.pieceCount, { disabledDecimal: true })} ${t('unit.piece')}`
                          : '-'}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={isFish ? 2 : 2.5}>
                    <Typography align="right" fontWeight={700} color="primary.main">
                      ฿{fNumber(el.priceAmount)}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
        </Box>
      </Box>
    </Box>
  )
}

export default ShrimpSalesRecordBody
